// Automation sequences — config-as-code (issue #224, epic #202/#230).
//
// This file IS the automation product: which sequences exist, which channel and
// delay each step uses, and the message copy. It is deliberately pure
// TypeScript — no Deno APIs, no imports — so the Deno worker imports it at
// runtime AND vitest/tsc validate it from `src/test/unit/automation-sequences.test.ts`.
//
// ┌─────────────────────────────────────────────────────────────────────────┐
// │ FRANK: the copy you edit lives in BUSINESS, EMAIL_COPY and SMS_COPY     │
// │ below. Everything else (footers, STOP suffix, quiet hours, sequence    │
// │ shape) is the compliance contract — change it only via a reviewed PR.  │
// └─────────────────────────────────────────────────────────────────────────┘
//
// CASL contract (enforced by the renderers, not by per-step copy):
//   * every email ends with a footer: business identity + postal mailing
//     address + an unsubscribe mechanism
//   * every SMS signs off with the sender name, then "Reply STOP to opt out."
//   * SMS only goes out 9am–8pm America/Toronto (quiet hours; the worker defers)

export type SendChannel = "email" | "sms";

export interface SequenceStep {
  /** Position in the sequence; contiguous from 0. */
  index: number;
  channel: SendChannel;
  /** Key into EMAIL_COPY / SMS_COPY. Stored on automation_sends rows. */
  templateKey: string;
  /** Send time as hours after enrollment creation. Step 0 is instant. */
  offsetHours: number;
}

export interface SequenceConfig {
  key: string;
  description: string;
  steps: SequenceStep[];
}

/** Variables available to message copy. */
export interface TemplateVars {
  /** Lead's first name; callers floor it to "there" when unknown. */
  firstName: string;
  /** Human event reference (e.g. EV-1042); empty for landing-page leads. */
  eventNumber: string;
  /** events.event_type ('wedding' | 'live_service' | …); '' when unknown. */
  eventType: string;
  /** Human phrase for the occasion ("your wedding") — derived by renderStep
   * from eventType; callers may pass "" and it will be filled in. */
  eventLabel: string;
  businessName: string;
  businessEmail: string;
  businessPhone: string;
  /** Optional menu link. ghl-lead-bridge fills it for a /menu/<slug> inquiry
   * (the menu they asked about) and for a plan-wizard lead (the menu for their
   * event type, #1221); automation-worker fills it for the wizard lead's Day-0
   * text. When both fields are present, every confirmation email variant
   * renders a "View the <menu>" CTA and the Day-0 SMS carries the link. */
  menuName?: string;
  menuUrl?: string;
}

// ---------------------------------------------------------------------------
// Copy variants — tailored conversations per event type (Frank, 2026-06-12)
// ---------------------------------------------------------------------------
// Each templateKey carries a `default` copy plus optional per-type overrides.
// Resolution: exact event-type variant → default. Types without their own
// variant (corporate, drop_off, landing-page 'other') read the default, which
// still names the occasion via `eventLabel`.

export type CopyVariant = "wedding" | "live_service" | "venue" | "default";

export function variantForEventType(eventType: string | null | undefined): CopyVariant {
  switch (eventType) {
    case "wedding":
      return "wedding";
    case "live_service":
      return "live_service";
    case "venue":
      return "venue";
    default:
      return "default";
  }
}

export function eventLabelFor(eventType: string | null | undefined): string {
  switch (eventType) {
    case "wedding":
      return "your wedding";
    case "live_service":
      return "your event";
    case "venue":
      return "your venue booking";
    case "corporate":
      return "your corporate event";
    case "drop_off":
      return "your order";
    default:
      return "your event";
  }
}

/** A default copy plus optional per-event-type overrides. */
interface VariantSet<T> {
  default: T;
  wedding?: T;
  live_service?: T;
  venue?: T;
}

function resolveVariant<T>(set: VariantSet<T>, variant: CopyVariant): T {
  if (variant === "default") return set.default;
  return set[variant] ?? set.default;
}

export interface RenderedEmail {
  channel: "email";
  subject: string;
  html: string;
}

export interface RenderedSms {
  channel: "sms";
  message: string;
}

export type RenderedMessage = RenderedEmail | RenderedSms;

// ---------------------------------------------------------------------------
// Business identity (confirmed by Frank 2026-06-12)
// ---------------------------------------------------------------------------
// postalAddress is REQUIRED by CASL in every commercial electronic message.
// name/email/phone can be overridden at runtime by site_branding identity.*.

export const BUSINESS = {
  name: "Tristano's Catering",
  email: "hello@tristanoscatering.ca",
  phone: "(289) 536-3126",
  postalAddress: "23 Queen St N, Bolton, ON L7E 1C1",
  /** Signs off every SMS so the sender is identified (CASL s.6(2)). A sign-off
   * at the end reads like a person texting; a "Name:" opener read like a bot
   * (Frank, 2026-10-08). Automated messages are signed "Emma" so the team can
   * tell at a glance which messages came from the assistant. */
  smsSenderName: "Emma at Tristano's Catering",
} as const;

/** Public host of the menu pages (`/menu/<slug>`) and the review-gate answer page (#521). */
export const PUBLIC_APP_URL = "https://app.tristanoscatering.ca";

/** #1221: plan-wizard event_type → published_menus.slug. The single copy; the
 * SPA imports it via src/lib/plan-menus.ts. */
export const MENU_SLUG_BY_EVENT_TYPE: Readonly<Record<string, string>> = {
  drop_off: "take-out-menu",
  corporate: "corporate-office-catering",
  wedding: "wedding-menu",
  venue: "event-space",
  live_service: "live-stations",
};

export function menuUrlForSlug(slug: string): string {
  return `${PUBLIC_APP_URL}/menu/${encodeURIComponent(slug)}`;
}

/** "Brunch Menu" → "Brunch Menu"; "Live Stations" → "Live Stations menu". Most
 * menu titles already end in "Menu", so don't render "Brunch Menu menu". */
export function menuPhrase(title: string): string {
  const t = title.trim();
  return /\bmenu$/i.test(t) ? t : `${t} menu`;
}

// ---------------------------------------------------------------------------
// Message copy — FRANK: EDIT ME (placeholder drafts, safe-if-sent).
// ---------------------------------------------------------------------------
// Bodies are plain paragraphs; the renderer wraps them and appends the CASL
// footer. `firstName`/`eventNumber` arrive pre-escaped — interpolate freely.

interface EmailCopy {
  subject: (v: TemplateVars) => string;
  /** Inner HTML paragraphs — no footer, the renderer appends it. */
  body: (v: TemplateVars) => string;
}

interface SmsCopy {
  /** Body of the SMS. The renderer appends the sender sign-off and the STOP suffix. */
  body: (v: TemplateVars) => string;
}

// All copy is signed "Emma" (see BUSINESS.smsSenderName note). Plain words,
// short sentences, friendly tone, no em dashes — Frank's spec 2026-06-13.
export const EMAIL_COPY: Record<string, VariantSet<EmailCopy>> = {
  // Step 0 — instant confirmation, replaces the Resend wizard confirmation.
  new_lead_confirmation_v1: {
    default: {
      subject: (v) => `We got your request, ${v.firstName}`,
      body: (v) => `
      <p>Hi ${v.firstName},</p>
      <p>Thanks for reaching out to ${v.businessName}! I'm Emma, and I help with
      events here. We got your request about ${v.eventLabel}${v.eventNumber ? ` (reference ${v.eventNumber})` : ""}.
      Someone from our team will be in touch within 24 hours. We'll go over
      menus, pricing, and your date.</p>
      ${menuCtaBlock(v)}
      <p>Have a question before then? Just reply to this email. It comes
      straight to us.</p>
      <p>Talk soon,<br/>Emma<br/>${v.businessName}</p>`,
    },
    wedding: {
      subject: (v) => `We got your wedding request, ${v.firstName}`,
      body: (v) => `
      <p>Hi ${v.firstName},</p>
      <p>Thank you for thinking of ${v.businessName} for your wedding! I'm Emma,
      and I help couples plan their big day. We got your request${v.eventNumber ? ` (reference ${v.eventNumber})` : ""}.
      Someone will reach out within 24 hours. We'll talk about your menu, a
      tasting, the service style, and your budget.</p>
      ${menuCtaBlock(v)}
      <p>Have a question before then? Just reply to this email. It comes
      straight to us.</p>
      <p>Talk soon,<br/>Emma<br/>${v.businessName}</p>`,
    },
    live_service: {
      subject: (v) => `We got your request, ${v.firstName}`,
      body: (v) => `
      <p>Hi ${v.firstName},</p>
      <p>Thanks for reaching out to ${v.businessName}! I'm Emma, and I help plan
      events. Chef stations, like a pasta bar or a carving station, are one of
      our favourite things to do. We got your request${v.eventNumber ? ` (reference ${v.eventNumber})` : ""}.
      Someone will be in touch within 24 hours to talk about stations, guest
      count, and timing.</p>
      ${menuCtaBlock(v)}
      <p>Have a question before then? Just reply to this email. It comes
      straight to us.</p>
      <p>Talk soon,<br/>Emma<br/>${v.businessName}</p>`,
    },
    venue: {
      subject: (v) => `We got your venue request, ${v.firstName}`,
      body: (v) => `
      <p>Hi ${v.firstName},</p>
      <p>Thanks for your interest in hosting your event in our space! I'm Emma,
      and I help with bookings. We got your request${v.eventNumber ? ` (reference ${v.eventNumber})` : ""}.
      Someone will check your date within 24 hours. We'll also walk you through
      the room, catering, and pricing.</p>
      ${menuCtaBlock(v)}
      <p>Have a question before then? Just reply to this email. It comes
      straight to us.</p>
      <p>Talk soon,<br/>Emma<br/>${v.businessName}</p>`,
    },
  },

  // Multi-day nurture is intentionally NOT hard-wired (Frank, 2026-06-13): only
  // the instant confirmation above is a fixed template. Everything after the
  // first response will be handled by the Emma follow-up agent — an AI that
  // reads the lead's context + replies and decides what to say and when, riding
  // these same send rails (kill switch, quiet hours, STOP/DND, send-once).
  // Tracked as a separate project. Until it lands, new_lead_v1 is a single
  // step: confirm, then complete.

  // Partial leads (#226): the ONE AND ONLY message an abandoned wizard gets.
  partial_lead_finish_quote_v1: {
    default: {
      subject: () => `Finish your quote request`,
      body: (v) => `
      <p>Hi ${v.firstName},</p>
      <p>It's Emma from ${v.businessName}. You started a request for
      ${v.eventLabel} but didn't finish. Your details are saved, so it only
      takes about a minute. Once you're done, we'll send a quote within 24
      hours.</p>
      <p><a href="https://app.tristanoscatering.ca/plan">Pick up where you left off</a></p>
      <p>Have a question? Just reply to this email.</p>
      <p>Talk soon,<br/>Emma<br/>${v.businessName}</p>`,
    },
  },
};

export const SMS_COPY: Record<string, VariantSet<SmsCopy>> = {
  // Day 0 instant text, sent alongside the confirmation email (Frank,
  // 2026-09-28). Initial response only, not a follow-up drip, so it sits
  // within the 2026-06-13 "no hard-wired follow-ups" call.
  // With a menu link (plan-wizard leads, #1221) the text carries it instead.
  new_lead_sms_instant_v1: {
    default: {
      body: (v) =>
        v.menuName && v.menuUrl
          ? // URL last, so the renderer's sign-off follows a line break, not
            // punctuation a linkifier could swallow. The renderer signs off
            // "- Emma at Tristano's Catering", so the brand isn't repeated.
            `Hi ${v.firstName}, thanks for reaching out! We'll be in touch within 24 hours. Here's our ${menuPhrase(v.menuName)}: ${v.menuUrl}`
          : `Hi ${v.firstName}, thanks for reaching out about ${v.eventLabel}! We'll be in touch within 24 hours with menus and pricing. Questions before then? Just reply here.`,
    },
  },
  // Available SMS template, intentionally NOT wired into a sequence step (no
  // hard-wired follow-ups — Frank, 2026-06-13). Kept so the SMS send rails and
  // the CASL contract (sender sign-off + "Reply STOP") stay exercised; the Emma
  // follow-up agent will compose SMS through this same renderer.
  new_lead_sms_day1_v1: {
    default: {
      body: (v) =>
        `Hi ${v.firstName}, thanks for your note about ${v.eventLabel}! We're putting some ideas together for you. Questions? Just reply here.`,
    },
  },
};

// ---------------------------------------------------------------------------
// Sequences
// ---------------------------------------------------------------------------

/** Sources whose fresh 'new' events the worker sweep may enroll. */
export const PUBLIC_LEAD_SOURCES = [
  "plan_wizard",
  "legacy_landing_page",
  "legacy_menu_inquiry",
] as const;

/** The sweep only looks back this far — older un-enrolled leads stay manual. */
export const SWEEP_MAX_AGE_HOURS = 24;

/**
 * A partial wizard lead (#226) counts as abandoned — and becomes eligible for
 * its single nudge email — only this long after its capture (the row is
 * written once, when name+email are typed), so a visitor who is still working
 * through the wizard minutes later is never emailed mid-session.
 */
export const PARTIAL_ABANDON_MIN_HOURS = 1;

export const NEW_LEAD_SEQUENCE_KEY = "new_lead_v1";
export const PARTIAL_LEAD_SEQUENCE_KEY = "partial_lead_v1";

export const SEQUENCES: Record<string, SequenceConfig> = {
  [NEW_LEAD_SEQUENCE_KEY]: {
    key: NEW_LEAD_SEQUENCE_KEY,
    description:
      "New public lead (wizard, landing page, menu inquiry): a Day 0 confirmation email tailored by event type, plus a Day 0 text when they gave a phone number (quiet hours apply). Wizard leads get the menu link for their service in both. Follow-up after this is handled by the Emma AI agent (separate project), not a hard-wired drip. Stops the moment staff engage (events.status leaves 'new').",
    steps: [
      { index: 0, channel: "email", templateKey: "new_lead_confirmation_v1", offsetHours: 0 },
      // Worker sends it on its next tick (every 5 min); ghl-lead-bridge
      // schedules step 1 at steps[1].offsetHours.
      { index: 1, channel: "sms", templateKey: "new_lead_sms_instant_v1", offsetHours: 0 },
    ],
  },
  [PARTIAL_LEAD_SEQUENCE_KEY]: {
    key: PARTIAL_LEAD_SEQUENCE_KEY,
    description:
      "Abandoned partial wizard lead (#226): at most ONE 'finish your quote?' email. Never SMS, never the full drip. Stops if the wizard is completed first.",
    steps: [
      { index: 0, channel: "email", templateKey: "partial_lead_finish_quote_v1", offsetHours: 0 },
    ],
  },
};

export function getSequence(key: string): SequenceConfig | null {
  return SEQUENCES[key] ?? null;
}

// ---------------------------------------------------------------------------
// Rendering — the CASL contract lives HERE, not in the editable copy
// ---------------------------------------------------------------------------

/** Minimal HTML escape for user-controlled values interpolated into email HTML. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Optional CTA block rendered inside every confirmation body when the lead has
 * a menu link (a /menu/<slug> inquiry, or a plan-wizard lead since #1221).
 * Empty string when either
 * field is missing — the plain confirmation renders unchanged so a menu
 * lookup failure in the bridge naturally degrades. menuName is escaped
 * because it comes from admin-editable `published_menus.title`; menuUrl
 * arrives already encodeURIComponent'd on the slug portion, so escapeHtml
 * on the whole URL is defense-in-depth (URL-safe chars pass through
 * escapeHtml unchanged) rather than a re-encode. Task #12 in the marketing
 * epic. Exported so send-event-confirmation's fallback email (#1235) renders
 * the same button.
 */
export function menuCtaBlock(v: Pick<TemplateVars, "menuName" | "menuUrl">): string {
  if (!v.menuName || !v.menuUrl) return "";
  const safeUrl = escapeHtml(v.menuUrl);
  const label = `View the ${escapeHtml(menuPhrase(v.menuName))}`;
  return `
      <p style="margin:20px 0">
        <a href="${safeUrl}"
           style="display:inline-block;background:#8b4513;color:#fff;padding:10px 18px;border-radius:6px;text-decoration:none;font-weight:600">
          ${label} →
        </a>
      </p>`;
}

export function emailFooter(v: TemplateVars): string {
  const phone = v.businessPhone ? ` · ${escapeHtml(v.businessPhone)}` : "";
  return `
      <hr style="border:none;border-top:1px solid #e5e5e5;margin:24px 0 12px"/>
      <p style="font-size:12px;color:#777;line-height:1.5">
        ${escapeHtml(v.businessName)} · ${escapeHtml(BUSINESS.postalAddress)}<br/>
        ${escapeHtml(v.businessEmail)}${phone}<br/>
        You're receiving this because you contacted ${escapeHtml(v.businessName)} about an event.
        To unsubscribe from these messages, reply with the word &quot;unsubscribe&quot; and we'll stop right away.
      </p>`;
}

/** Suffix appended to EVERY outbound SMS (CASL opt-out mechanism). */
export const SMS_STOP_SUFFIX = "Reply STOP to opt out.";

/**
 * Render a step into a sendable message. The CASL pieces (email footer with
 * identity + postal address + unsubscribe; SMS sender sign-off + STOP suffix)
 * are appended unconditionally — editable copy cannot remove them.
 * Returns null when the step's templateKey has no copy (config bug; the
 * worker treats it as a hard error rather than sending something half-built).
 */
export function renderStep(step: SequenceStep, rawVars: TemplateVars): RenderedMessage | null {
  const variant = variantForEventType(rawVars.eventType);
  const eventLabel = eventLabelFor(rawVars.eventType);
  // Escape user-controlled fields once, centrally; copy interpolates freely.
  const v: TemplateVars = {
    ...rawVars,
    firstName: escapeHtml(rawVars.firstName || "there"),
    eventNumber: escapeHtml(rawVars.eventNumber ?? ""),
    eventLabel,
  };

  if (step.channel === "email") {
    const copySet = EMAIL_COPY[step.templateKey];
    if (!copySet) return null;
    const copy = resolveVariant(copySet, variant);
    const html = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;color:#222;line-height:1.6;max-width:560px">
      ${copy.body(v)}
      ${emailFooter(v)}
    </div>`;
    return { channel: "email", subject: copy.subject(v), html };
  }

  const copySet = SMS_COPY[step.templateKey];
  if (!copySet) return null;
  const copy = resolveVariant(copySet, variant);
  // SMS is plain text — use the unescaped first name.
  const smsVars: TemplateVars = { ...rawVars, firstName: rawVars.firstName || "there", eventLabel };
  return {
    channel: "sms",
    message: `${copy.body(smsVars)}\n- ${BUSINESS.smsSenderName}\n${SMS_STOP_SUFFIX}`,
  };
}

// ---------------------------------------------------------------------------
// Quiet hours — SMS only goes out 9:00–20:00 America/Toronto
// ---------------------------------------------------------------------------

export const QUIET_HOURS = {
  timeZone: "America/Toronto",
  /** First hour (inclusive) SMS may be sent. */
  startHour: 9,
  /** First evening hour (exclusive bound) SMS may no longer be sent. */
  endHourExclusive: 20,
} as const;

/** Hour-of-day (0–23) and minute in America/Toronto for a given instant (DST-safe). */
export function torontoClock(at: Date): { hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: QUIET_HOURS.timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(at);
  const get = (type: string): number => {
    const part = parts.find((p) => p.type === type);
    return part ? parseInt(part.value, 10) : 0;
  };
  // Some engines render midnight as "24" with hour12:false — normalize.
  return { hour: get("hour") % 24, minute: get("minute") };
}

export function isWithinSmsSendWindow(at: Date): boolean {
  const { hour } = torontoClock(at);
  return hour >= QUIET_HOURS.startHour && hour < QUIET_HOURS.endHourExclusive;
}

/**
 * Next instant at/after `at` that falls inside the SMS send window (the next
 * 9:00 Toronto when outside it). Computed by minute arithmetic on the Toronto
 * wall clock; a DST boundary inside the wait can land ±1h off, which is fine —
 * the worker re-checks isWithinSmsSendWindow before sending and re-defers.
 */
export function nextSmsSendTime(at: Date): Date {
  const { hour, minute } = torontoClock(at);
  if (hour >= QUIET_HOURS.startHour && hour < QUIET_HOURS.endHourExclusive) return at;
  const nowMinutes = hour * 60 + minute;
  const targetMinutes = QUIET_HOURS.startHour * 60;
  const deltaMinutes =
    hour < QUIET_HOURS.startHour
      ? targetMinutes - nowMinutes // later this morning
      : 24 * 60 - nowMinutes + targetMinutes; // 9:00 tomorrow
  return new Date(at.getTime() + deltaMinutes * 60_000);
}
